import { ReactNode, RefObject } from "react"
import { VStack, HStack, Icon, Text, Progress } from "@chakra-ui/react"
import Dialog from "./Dialog"
import { FaCheck, FaTriangleExclamation } from "react-icons/fa6"
import { ActionButton, CancelButton } from "../content/components/Button"

// Workflow states for the status toggle process
export type WorkflowState =
  | "idle"
  | "confirming"
  | "processing"
  | "cancelling"
  | "success"
  | "confirmed"
  | "error"
  | "conflict"

export interface WorkflowMessages {
  processing: string
  cancelling?: string
  success: string
  error: string
  conflict?: string
}

export interface StatusToggleWorkflowProps {
  // Current workflow state
  workflowState: WorkflowState

  // Action being performed
  pendingAction: "activate" | "deactivate" | null

  // Messages for different states
  messages: WorkflowMessages | null

  // Callbacks
  onConfirm: () => void
  onCancel?: () => void
  onClose: () => void

  // Optional container for dialog positioning
  container?: RefObject<HTMLElement | null>

  // Custom content for confirmation dialog
  confirmationContent?: ReactNode

  // Custom title for different states
  customTitles?: Partial<Record<WorkflowState, string>>

  // Skip button functionality
  showSkipButton?: boolean
  onSkip?: () => void
}

const StatusToggleWorkflow = ({
  workflowState,
  pendingAction,
  messages,
  onConfirm,
  onCancel,
  onClose,
  container,
  confirmationContent,
  customTitles = {},
  showSkipButton = true,
  onSkip,
}: StatusToggleWorkflowProps) => {
  const isDialogOpen = workflowState !== "idle" && workflowState !== "confirmed"

  // Helper functions for Dialog component
  const getDialogTitle = (): string => {
    if (customTitles[workflowState]) {
      return customTitles[workflowState]!
    }

    switch (workflowState) {
      case "confirming":
        return `${pendingAction === "activate" ? "Activate" : "Deactivate"} monitoring`
      case "processing":
        return "Processing Request"
      case "cancelling":
        return "Cancelling Request"
      case "success":
        return "Success"
      case "confirmed":
        return "Confirmed"
      case "error":
        return "Error"
      case "conflict":
        return "Conflict"
      default:
        return ""
    }
  }

  const getDialogContent = () => {
    switch (workflowState) {
      case "confirming":
        return (
          confirmationContent ||
          (pendingAction ? (
            <Text>
              Are you sure you want to <strong>{pendingAction}</strong> this?
            </Text>
          ) : null)
        )

      case "processing":
        return messages ? (
          <VStack align="start" gap={3} width="100%">
            <Text>{messages.processing}</Text>
            <Progress.Root value={null} width="100%">
              <Progress.Track>
                <Progress.Range />
              </Progress.Track>
            </Progress.Root>
          </VStack>
        ) : null

      case "cancelling":
        return messages?.cancelling ? (
          <VStack align="start" gap={3} width="100%">
            <Text>{messages.cancelling}</Text>
            <Progress.Root value={null} width="100%">
              <Progress.Track>
                <Progress.Range />
              </Progress.Track>
            </Progress.Root>
          </VStack>
        ) : null

      case "success":
        return messages ? (
          <HStack gap={3}>
            <Icon as={FaCheck} color="green.500" />
            <Text>{messages.success}</Text>
          </HStack>
        ) : null

      case "confirmed":
        // The confirmed state should not render content - it's a transition state
        return null

      case "error":
        return messages ? (
          <HStack gap={3}>
            <Icon as={FaTriangleExclamation} color="red.500" />
            <Text>{messages.error}</Text>
          </HStack>
        ) : null

      case "conflict":
        return messages?.conflict ? (
          <HStack gap={3}>
            <Icon as={FaTriangleExclamation} color="orange.500" />
            <Text>{messages.conflict}</Text>
          </HStack>
        ) : null

      default:
        return null
    }
  }

  const getDialogFooter = () => {
    switch (workflowState) {
      case "confirming":
        return (
          <>
            {onCancel && (
              <CancelButton onClick={onCancel}>
                {pendingAction === "deactivate" ? "Do not deactivate" : "No"}
              </CancelButton>
            )}
            <ActionButton
              context={{ label: "Yes", description: "Confirm action", variant: "primary" }}
              onClick={onConfirm}
            />
          </>
        )

      case "success":
      case "error":
      case "conflict":
        return (
          <ActionButton context={{ label: "OK", description: "Close dialog", variant: "primary" }} onClick={onClose} />
        )

      case "processing":
        return (
          <>
            {pendingAction === "activate" && onCancel && <CancelButton onClick={onCancel} />}
            {showSkipButton && onSkip && (
              <ActionButton
                context={{
                  label: "Skip validation",
                  description: "Skip IoT data validation and keep monitoring active",
                  variant: "secondary",
                }}
                onClick={onSkip}
              />
            )}
          </>
        )

      case "cancelling":
        return null // No buttons during cancellation

      default:
        return null
    }
  }

  return (
    <Dialog
      title={getDialogTitle()}
      open={isDialogOpen}
      setOpen={onClose}
      loading={workflowState === "processing" || workflowState === "cancelling"}
      disabled={false}
      footer={getDialogFooter()}
      container={container}
      showCloseButton={false} // Hide close button - users should use action buttons
    >
      {getDialogContent()}
    </Dialog>
  )
}

export default StatusToggleWorkflow
